import "swiper/css";
import "swiper/css/effect-fade";

import Image from "next/image";
import Link from "next/link";
import { useState, useEffect } from "react";
import dayjs, { Dayjs } from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";

import { AppointmentPicker } from "@/src/component/partial/AppointmentPicker";
import { FlowSpinner } from "@/src/component/partial/FlowSpinner";
import { Form } from "../../../../partial/BuildDynamicForm/component";
import { BaseButton } from "@/src/component/base/BaseButton";
import { ImgBgResponsive } from "@/src/component/base/ImgBgResponsive";
import { FormHooks, ProviderHooks } from "@/src/hook";
import { TourLogic } from "@/src/logic";
import { FormPropertyType } from "@/src/logic/DynamicFormLogic";
import { FrontendTrackingService } from "@/src/service";
import { StrapiComponentType, ComponentType } from "@/src/type";
import { LoadFromCDN, Log } from "@/src/util";

//TODO: refactor these components to __modules__ partial components
import { SectionModule, TextModule } from "@/src/component/partial/__module__";

import styles from "./ScheduleTour.module.scss";

interface ScheduleTourProps extends ComponentType.DynamicPageSectionComponentProps<StrapiComponentType.Section.ScheduleTour> {}

dayjs.extend(utc);
dayjs.extend(timezone);

function ScheduleTour({
  lazy,
  section,
  slug,
  snapToGrid,
  bottomDrawerHeight,
  searchParams,
  mobileHint,
  deviceWidthHint,
  quality,
  layout,
  calendarConfig,
}: ScheduleTourProps) {
  const scheduleTour: StrapiComponentType.Section.ScheduleTour | undefined = section.type?.[0];
  const calendarSettings = calendarConfig?.data?.attributes?.specificSettings?.[0];
  const destinations = scheduleTour?.destinations;
  const { crmLinkId, submitForm } = FormHooks.useDynamicForm({
    destinations: (destinations?.data ?? []).map(({ attributes }) => attributes) ?? [],
  });

  const [availableTimeSlots, setAvailableTimeSlots] = useState<any>([]);
  const [selectedTime, setSelectedTime] = useState<Dayjs | null>(null);
  const [appointmentDateTimeConfirmed, setAppointmentDateTimeConfirmed] = useState(false);
  const [isTourSubmitted, setIsTourSubmitted] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const cms = ProviderHooks.useCMS();
  const toast = ProviderHooks.useToast();

  /**
   * Track when user lands on this page as tour_start
   */
  useEffect(() => {
    if (!crmLinkId) {
      return;
    }

    const action = "tour_start";
    FrontendTrackingService.postTrackingLog({
      action: action,
      actionId: crmLinkId,
      actionIdType: "crmLinkId",
      actionMessage: "Schedule tour screen loaded",
      triggeredFrom: "ScheduleTour.tsx",
      trackingEvents: [
        {
          gaEventName: "tour_start",
          fbEventName: "tour_start",
          googleAdsConversion: false,
        },
      ],
    });
  }, [crmLinkId]);

  useEffect(() => {
    (async () => {
      try {
        if (!scheduleTour || !calendarSettings?.__component) {
          Log.logToProvider(Log.LogLevel.ERROR, "DynamicPage/component/ScheduleTour.tsx", "No schedule tour or calendar settings", [
            { scheduleTour, calendarSettings },
          ]);
          toast.addMessage({
            title: "Error getting available time slots",
            subtitle: "Please try again later",
            type: "error",
          });
          return;
        }

        setIsLoading(true);
        const integrationType = calendarSettings?.__component === "object.google-calendar" ? "google" : "yardi";
        const response = await TourLogic.getEvents(integrationType, calendarSettings);
        const _events = response.response as any[];

        const availableTimeSlots =
          calendarSettings?.__component === "object.google-calendar"
            ? TourLogic.getAvailableTimeSlots(calendarSettings, [..._events])
            : TourLogic.getAvailableTimeSlotsYardi(_events, calendarSettings?.numberOfTourDays ?? 14);

        setAvailableTimeSlots(availableTimeSlots ?? []);
        if ((availableTimeSlots ?? []).length === 0) {
          toast.addMessage({
            title: "No available time slots",
            subtitle: "Please try again later",
            type: "error",
          });
          return;
        }

        let initialSelectedTourTime: Dayjs | null = null;
        if (searchParams && searchParams?.["timestamp"]) {
          for (let i = 0; i <= (availableTimeSlots ?? []).length; i++) {
            initialSelectedTourTime = (availableTimeSlots?.[i]?.times ?? []).find((date) => date.format() === searchParams["timestamp"]) || null;
            if (initialSelectedTourTime) {
              setAppointmentDateTimeConfirmed(true);
              break;
            }
          }
        } else {
          initialSelectedTourTime = availableTimeSlots?.[0]?.times?.[0] ?? null;
        }

        setSelectedTime(initialSelectedTourTime);
      } catch (err) {
        toast.addMessage({
          title: "Error getting available time slots",
          subtitle: "Please try again later",
          type: "error",
        });
        Log.logToProvider(
          Log.LogLevel.ERROR,
          "DynamicPage/component/ScheduleTour.tsx(useEffect)",
          "Get calendar events",
          [err],
          "Error getting available time slots"
        );
      }
      setIsLoading(false);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [calendarSettings, scheduleTour, searchParams]);

  function getFormProperties(formData: Record<string, any>, selectedTime: Dayjs) {
    const formProperties: FormPropertyType[] = [];
    formProperties.push(
      {
        property: "scheduleTour",
        value: "true",
        syncToCRM: "false",
      },
      {
        property: "scheduleTourUnix",
        value: selectedTime.unix() ? `${selectedTime.unix()}` : "",
        syncToCRM: "false",
      },
      {
        property: "scheduleTourUtcTime",
        value: selectedTime?.utc().format() ? `${selectedTime?.utc().format()}` : "",
        syncToCRM: "false",
      },
      {
        property: "scheduleTourPropertyLocalTime",
        value: selectedTime?.format() ? `${selectedTime?.format()}` : "",
        syncToCRM: "false",
      }
    );

    if (formData && Object.keys(formData).length > 0) {
      for (const [key, value] of Object.entries(formData)) {
        formProperties.push({
          property: key,
          value: value,
          syncToCRM: "false",
        });
      }
    }
    return formProperties;
  }

  function getCommunity() {
    switch (cms?.propertySlug) {
      case "riyadh-narjis":
        return "Flow Narjis";
      case "riyadh-granada":
        return "Flow Granada";
      case "miami":
        return "Miami";
      case "flowhouse-miami":
        return "Flowhouse Miami";
      case "fort-lauderdale":
        return "Fort Lauderdale";
      default:
        return "";
    }
  }

  async function handleSubmit(formData: Record<string, any>) {
    setIsSubmitting(true);
    if (!selectedTime) {
      toast.addMessage({
        title: "No tour time selected",
        subtitle: "Please select a tour time and try again",
        type: "error",
      });
      setIsSubmitting(false);
      return;
    }

    //save the event to the calendar
    let scheduleResponse;
    try {
      const attendeeInfo = TourLogic.getAttendeeInfo(formData);
      if (calendarSettings?.__component === "object.google-calendar") {
        scheduleResponse = await TourLogic.createGoogleCalendarEvent(calendarSettings, attendeeInfo, selectedTime);
      }

      if (calendarSettings?.__component === "object.yardi-calendar") {
        scheduleResponse = await TourLogic.createYardiCalendarEvent(formData, attendeeInfo, selectedTime, calendarSettings);
      }
    } catch (err) {
      Log.logToProvider(
        Log.LogLevel.ERROR,
        "DynamicPage/component/ScheduleTour.tsx(handleSubmit)",
        "Unable to save event to the calendar",
        [err],
        "Error submitting tour"
      );
    }

    //save the event to the CRM
    const trackingEventOverrides = [
      {
        gaEventName: "tour_submit",
        fbEventName: "tour_submit",
        googleAdsConversion: true,
      },
      {
        gaEventName: "Lead-Submit",
        fbEventName: "Lead-Submit",
        googleAdsConversion: false,
      },
    ];
    const _trackingEvents = (scheduleTour?.trackingEvents ?? []).length > 0 ? scheduleTour?.trackingEvents : trackingEventOverrides;
    formData["Community"] = getCommunity();
    formData["Tour Date"] = selectedTime?.format("MM/DD/YYYY");
    formData["Tour Status"] = "Scheduled";
    const metadata = {
      scheduleTour: "true",
      scheduleTourUnix: selectedTime.unix() ? `${selectedTime.unix()}` : "",
      scheduleTourLocalTime: selectedTime?.format() ? `${selectedTime?.format()}` : "",
      scheduleTourUtcTime: selectedTime?.utc().format() ? `${selectedTime?.utc().format()}` : "",
      community: getCommunity(),
    };

    await submitForm({
      formData,
      currentStep: 0,
      extraMetadata: metadata,
      extraFormProperties: getFormProperties(formData, selectedTime),
      trackingEventOverrides: _trackingEvents,
    });

    if (!scheduleResponse?.success) {
      toast.addMessage({
        title: "Error submitting tour",
        subtitle: "Please try again later",
        type: "error",
      });
      Log.logToProvider(
        Log.LogLevel.ERROR,
        "DynamicPage/component/ScheduleTour.tsx(handleSubmit)",
        "Submit tour",
        [scheduleResponse],
        "Error submitting tour"
      );
    } else {
      setIsTourSubmitted(scheduleResponse.success);
    }
    setIsSubmitting(false);
  }

  if (!scheduleTour || !calendarSettings?.__component) {
    return <></>;
  }

  return (
    <SectionModule
      section={section}
      slug={slug}
      classes={{
        styledContainer: styles.container,
      }}
      stylePrefix="ScheduleTour"
      lazy={lazy}
      styleContainerId={scheduleTour?.id}
      snapToGrid={snapToGrid}
      bottomDrawerHeight={bottomDrawerHeight}
      searchParams={searchParams}
      mobileHint={mobileHint}
      deviceWidthHint={deviceWidthHint}
      quality={quality}
      layout={layout}
    >
      {isLoading ? (
        <FlowSpinner
          height={230}
          width={230}
        />
      ) : (
        <LocalizationProvider dateAdapter={AdapterDateFns}>
          {!appointmentDateTimeConfirmed && availableTimeSlots.length > 0 ? (
            <div style={{ display: "flex", flexDirection: "column", gap: 16, width: "100%", maxWidth: "800px" }}>
              <AppointmentPicker
                datePickerTitle={scheduleTour?.title}
                datePickerSubtitle={scheduleTour?.subtitle}
                availableTimeSlots={availableTimeSlots}
                initialTime={selectedTime}
                spaceBetween={6}
                timePickerTitle={scheduleTour?.timePickerTitle}
                onChange={(date, time) => {
                  setSelectedTime(time ?? null);
                }}
              />
              <div className={styles.nextButtonContainer}>
                <BaseButton
                  variant="contained"
                  color={scheduleTour?.nextButton?.color ?? "primary"}
                  size={scheduleTour?.nextButton?.size || "large"}
                  type={scheduleTour?.nextButton?.type || "button"}
                  onClick={() => {
                    setAppointmentDateTimeConfirmed(true);
                  }}
                  disabled={!selectedTime}
                >
                  {scheduleTour?.nextButton?.label}
                </BaseButton>
              </div>
            </div>
          ) : null}
          {appointmentDateTimeConfirmed && !isTourSubmitted ? (
            <div className={styles.formContainer}>
              <div className={styles.selectedDateInfoContainer}>
                <div>
                  <TextModule
                    className={styles.selectedDateLabel}
                    text={{ tag: "body1", text: scheduleTour.selectedDateLabel }}
                  />
                  <TextModule
                    className={styles.selectedTime}
                    text={{ tag: "body1", text: selectedTime?.format("MMM D, YYYY @ h:mma") }}
                  />
                </div>
                <div className={styles.editSelectedDateButtonContainer}>
                  <BaseButton
                    startIcon={
                      <Image
                        src="/image/icon_edit_dark.svg"
                        alt="Edit Icon"
                        width={24}
                        height={24}
                        style={{ top: "8px" }}
                      />
                    }
                    disableFocusRipple={true}
                    disableRipple={true}
                    onClick={() => {
                      setAppointmentDateTimeConfirmed(false);
                    }}
                    className={styles.editSelectedDateButton}
                  >
                    <span className={styles.editSelectedDateButtonText}>Edit</span>
                  </BaseButton>
                </div>
              </div>
              <div className={styles.divider} />
              <TextModule
                className={styles.formTitle}
                text={{ tag: "h2", text: scheduleTour.formTitle }}
              />
              <TextModule
                className={styles.formSubtitle}
                text={{ tag: "body1", text: scheduleTour.formSubtitle }}
              />
              <Form
                isDark={true}
                onSubmit={handleSubmit}
                formId="SubmitTour"
                form={{
                  submitButtonLabel: "Submit",
                }}
                step={{
                  form_items: scheduleTour.formItems,
                }}
                crmLinkId={crmLinkId}
                {...(scheduleTour?.formCta && {
                  renderSubmitButton: () => {
                    return (
                      <BaseButton
                        type="submit"
                        variant="contained"
                        color={scheduleTour?.formCta?.color || "primary"}
                        size={scheduleTour?.formCta?.size || "large"}
                        loading={isSubmitting}
                      >
                        {scheduleTour?.formCta?.label}
                      </BaseButton>
                    );
                  },
                })}
                renderHiddenInputs={
                  selectedTime && (
                    <>
                      <input
                        type="text"
                        name="scheduleTour"
                        value="true"
                      />
                      <input
                        type="text"
                        name="scheduleTourUnix"
                        value={selectedTime?.unix() ? `${selectedTime?.unix()}` : ""}
                      />
                      <input
                        type="text"
                        name="scheduleTourUtcTime"
                        value={selectedTime?.utc()?.format() ? `${selectedTime?.utc()?.format()}` : ""}
                      />
                      <input
                        type="text"
                        name="scheduleTourLocalTime"
                        value={selectedTime?.format() ? `${selectedTime?.format()}` : ""}
                      />
                      {cms?.propertySlug && (
                        <input
                          type="text"
                          name="propertySlug"
                          value={cms?.propertySlug}
                        />
                      )}
                    </>
                  )
                }
              />
            </div>
          ) : null}
          {isTourSubmitted ? (
            <div className={styles.confirmationContainer}>
              <Image
                src="/image/icon_check_circle_dark.svg"
                alt="Check Circle Icon"
                width={68}
                height={68}
                className={styles.confirmationIcon}
              />
              <TextModule
                className={styles.confirmationTitle}
                text={{ tag: "h2", text: scheduleTour.confirmationTitle }}
              />
              <TextModule
                className={styles.confirmationSubtitle}
                text={{ tag: "body1", text: scheduleTour.confirmationSubtitle }}
              />
              <TextModule
                className={styles.confirmationTourTime}
                text={{ tag: "body1", text: selectedTime?.format("dddd, MMM D, YYYY @ h:mma") }}
              />
              {scheduleTour.confirmationPromoCards?.length ? (
                <div style={{ width: "100%" }}>
                  <TextModule
                    className={styles.confirmationPromoTitle}
                    text={{ tag: "h3", text: scheduleTour.confirmationPromoTitle }}
                  />
                  <div className={styles.confirmationPromoCardsContainer}>
                    {scheduleTour.confirmationPromoCards.map((card) => {
                      const promoCardCta = card.link;
                      return (
                        <Link
                          key={card.image.data.attributes.url}
                          href={promoCardCta?.href || "#"}
                          target={promoCardCta?.target || "_self"}
                          className={styles.confirmationPromoCard}
                        >
                          <ImgBgResponsive
                            imageUrl={LoadFromCDN.nextGenFormat(card.image.data.attributes.url)}
                            className={styles.confirmationPromoCardImage}
                          >
                            <div className={styles.confirmationPromoCardInnerContainer}>
                              <TextModule text={{ tag: "body1", text: card.title, color: "secondaryMain", fontSize: 20 }} />
                            </div>
                          </ImgBgResponsive>
                        </Link>
                      );
                    })}
                  </div>
                </div>
              ) : null}
            </div>
          ) : null}
        </LocalizationProvider>
      )}
    </SectionModule>
  );
}

export default ScheduleTour;
